// Member register contract shared by the API and the web app.
// Enum values are UPPERCASE (engineering safeguard 3); filter values are lower-case URL tokens.

export const HOUSE_STATUSES = ['ACTIVE', 'MOVED_OUT'] as const;
export type HouseStatus = (typeof HOUSE_STATUSES)[number];

export const RATE_PLANS = ['STANDARD', 'NRI', 'EXEMPT'] as const;
export type RatePlan = (typeof RATE_PLANS)[number];

/** Register consent for the house, cached from its consent history (pilot slice 5). */
export const CONSENT_STATUSES = ['GIVEN', 'PENDING', 'WITHDRAWN'] as const;
export type ConsentStatus = (typeof CONSENT_STATUSES)[number];

export const RESIDENCES = ['RESIDENT', 'OTHER_STATE', 'ABROAD'] as const;
export type Residence = (typeof RESIDENCES)[number];

export const RELATIONS = ['HEAD', 'WIFE', 'HUSBAND', 'SON', 'DAUGHTER', 'FATHER', 'MOTHER', 'OTHER'] as const;
export type Relation = (typeof RELATIONS)[number];

export const MEMBER_STATUSES = ['ALIVE', 'DECEASED', 'MARRIED_OUT', 'TRANSFERRED'] as const;
export type MemberStatus = (typeof MEMBER_STATUSES)[number];

/** Residence filter on the house list (URL tokens). */
export const RESIDENCE_FILTERS = ['abroad', 'other', 'resident'] as const;
export type ResidenceFilter = (typeof RESIDENCE_FILTERS)[number];

/**
 * Dashboard tiles on Member Management. A tile's count and the grid it opens come from the
 * same predicate over the same base set (CampusTrack rule, 14 Sep 2026).
 */
export const HOUSE_TILES = ['all', 'pravasi', 'other', 'phone', 'consent', 'arrears'] as const;
export type HouseTile = (typeof HOUSE_TILES)[number];

export const PAGE_SIZES = [10, 25, 50] as const;
export type PageSize = (typeof PAGE_SIZES)[number];

/** Every query key the house list accepts; anything else is refused with 400. */
export const HOUSE_LIST_FILTERS = ['ward', 'status', 'residence', 'tile', 'q', 'page', 'size'] as const;
/** The summary uses the same base filters, without tile or paging. */
export const HOUSE_SUMMARY_FILTERS = ['ward', 'status', 'residence', 'q'] as const;

export interface WardRef {
  code: string;
  nameEn: string;
  nameMl: string | null;
}

export interface Ward extends WardRef {
  id: string;
}

export interface HouseRow {
  id: string;
  houseNo: string;
  nameEn: string;
  nameMl: string | null;
  ward: WardRef;
  headName: string | null;
  memberCount: number;
  abroadCount: number;
  otherStateCount: number;
  ratePlan: RatePlan;
  monthlyRateInr: number;
  arrearsMonths: number;
  arrearsInr: number;
  phoneVerified: boolean;
  consent: ConsentStatus;
  status: HouseStatus;
}

export interface HouseList {
  rows: HouseRow[];
  total: number;
  page: number;
  size: number;
}

export interface HouseSummary {
  houses: number;
  members: number;
  pravasi: number;
  other: number;
  phone: number;
  consent: number;
  arrears: number;
}

export interface Member {
  id: string;
  nameEn: string;
  nameMl: string | null;
  relation: Relation;
  birthYear: number | null;
  gender: 'M' | 'F' | null;
  residence: Residence;
  country: string | null;
  region: string | null;
  city: string | null;
  work: string | null;
  phone: string | null;
  status: MemberStatus;
  /** Set when contact details were erased on request (pilot slice 5); the register entry stays. */
  detailsErasedAt: string | null;
  /** Send back unchanged when editing; a different version means someone else changed it. */
  version: number;
}

export interface HouseDetail extends HouseRow {
  ownership: 'OWN' | 'RENTED' | null;
  members: Member[];
  version: number;
}

// ── Editing (pilot slice 3) ──

export const OWNERSHIPS = ['OWN', 'RENTED'] as const;
export type Ownership = (typeof OWNERSHIPS)[number];
export const GENDERS = ['M', 'F'] as const;

/** Text limits shared by forms and the API. */
export const LIMITS = { name: 80, place: 60, work: 60, wardCode: 20 } as const;

export interface MemberInput {
  nameEn: string;
  nameMl?: string | null;
  relation: Relation;
  birthYear?: number | null;
  gender?: 'M' | 'F' | null;
  residence: Residence;
  country?: string | null;
  region?: string | null;
  city?: string | null;
  work?: string | null;
  /** Any common format; stored as E.164 (+91… for a bare 10-digit Indian mobile). */
  phone?: string | null;
}

export interface HouseCreate {
  wardCode: string;
  nameEn: string;
  nameMl?: string | null;
  ownership?: Ownership | null;
  ratePlan: RatePlan;
  monthlyRateInr: number;
  /** The head of the family is created with the house. */
  head: Omit<MemberInput, 'relation'>;
}

export interface HousePatch {
  version: number;
  nameEn?: string;
  nameMl?: string | null;
  ownership?: Ownership | null;
  ratePlan?: RatePlan;
  monthlyRateInr?: number;
  status?: HouseStatus;
}

export type MemberPatch = Partial<MemberInput> & { version: number; status?: MemberStatus };

export interface WardInput {
  code: string;
  nameEn: string;
  nameMl?: string | null;
}
